"use client";

import React from "react";
import { createPortal } from "react-dom";
import { useEffect, useRef, useState } from "react";
import styles from "./styles.module.css";
import InputField from "../input";
import { FaChevronDown } from "react-icons/fa";

// Helper to extract text from React children recursively
function extractTextFromChildren(children) {
  let text = "";
  React.Children.forEach(children, (child) => {
    if (typeof child === "string" || typeof child === "number") {
      text += child;
    } else if (child && child.props && child.props.children) {
      text += extractTextFromChildren(child.props.children);
    }
  });
  return text;
}

// Create a context to share state between components
const SelectContext = React.createContext(undefined);

// Helper function to get computed z-index
const getComputedZIndex = (element) => {
  if (!element) return 0;
  const zIndex = window.getComputedStyle(element).zIndex;
  return zIndex === "auto"
    ? getComputedZIndex(element.parentElement)
    : parseInt(zIndex, 10);
};

export function Select({
  placeholder = "Select an option",
  onChange,
  value = "",
  disabled = false,
  className = "",
  children,
}) {
  const [selectedValue, setSelectedValue] = useState(value);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
  const [zIndex, setZIndex] = useState(0);
  const [search, setSearch] = useState(""); // <-- Add search state
  const [dropdownDirection, setDropdownDirection] = useState("below"); // 'below' or 'above'
  const [dropdownTop, setDropdownTop] = useState(0); // actual top for dropdown
  const selectRef = useRef(null);
  const contentRef = useRef(null);

  // Update position when dropdown opens or window resizes
  const updatePosition = () => {
    if (selectRef.current) {
      const rect = selectRef.current.getBoundingClientRect();
      const scrollY = window.scrollY;
      const scrollX = window.scrollX;
      const triggerTop = rect.top + scrollY;
      const triggerBottom = rect.bottom + scrollY;
      const triggerLeft = rect.left + scrollX;
      const triggerWidth = rect.width;
      const windowHeight = window.innerHeight;
      const spaceBelow = windowHeight - rect.bottom;
      const spaceAbove = rect.top;
      let contentHeight = 0;
      if (contentRef.current) {
        contentRef.current.style.visibility = "hidden";
        contentRef.current.style.display = "block";
        contentHeight = contentRef.current.offsetHeight;
        contentRef.current.style.visibility = "";
        contentRef.current.style.display = "";
      } else {
        // fallback: estimate or use a default height
        contentHeight = 200;
      }
      let direction = "below";
      let top = triggerBottom;
      if (spaceBelow < contentHeight && spaceAbove > contentHeight) {
        direction = "above";
        top = triggerTop - contentHeight;
      }
      setPosition({
        top: triggerBottom,
        left: triggerLeft,
        width: triggerWidth,
      });
      setDropdownDirection(direction);
      setDropdownTop(top);
      // Update z-index
      const parentZIndex = getComputedZIndex(selectRef.current);
      setZIndex(parentZIndex + 100);
    }
  };

  // Ensure initial position is updated after render when opened
  useEffect(() => {
    if (!open) return;
    const timeoutId = setTimeout(() => {
      updatePosition();
    }, 0);
    return () => clearTimeout(timeoutId);
  }, [open]);

  // Reset search when dropdown opens
  useEffect(() => {
    if (open) {
      setSearch("");
    }
  }, [open]);
useEffect(() => {
  setSelectedValue(value);
}, [value]);

  // Handle outside clicks to close the dropdown
  useEffect(() => {
    const handleOutsideClick = (event) => {
      const isClickInside =
        selectRef.current?.contains(event.target) ||
        contentRef.current?.contains(event.target);

      if (!isClickInside) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  // Update position on scroll and resize
  useEffect(() => {
    if (open) {
      const handleScroll = () => {
        updatePosition();
      };

      const handleResize = () => {
        updatePosition();
      };

      window.addEventListener("scroll", handleScroll, true);
      window.addEventListener("resize", handleResize);

      // Initial position update
      updatePosition();

      return () => {
        window.removeEventListener("scroll", handleScroll, true);
        window.removeEventListener("resize", handleResize);
      };
    }
  }, [open]);

  // Handle value changes
  const handleChange = (newValue) => {
    setSelectedValue(newValue);
    onChange?.(newValue);
    setOpen(false);
  };

  return (
    <SelectContext.Provider
      value={{
        value: selectedValue,
        onChange: handleChange,
        open,
        setOpen,
        selectRef,
        contentRef,
        position,
        zIndex,
        search, // <-- Add to context
        setSearch, // <-- Add to context
        dropdownDirection, // <-- Add to context
        dropdownTop, // <-- Add to context
        disabled, // source of truth (data-disabled is read during render and lags toggles)
      }}
    >
      <div
        ref={selectRef}
        className={`${styles.select} ${className}`}
        data-disabled={disabled}
        data-state={open ? "open" : "closed"}
      >
        {children}
      </div>
    </SelectContext.Provider>
  );
}

export function SelectTrigger({ className = "", children, style }) {
  const context = React.useContext(SelectContext);

  if (!context) {
    throw new Error("SelectTrigger must be used within a Select");
  }

  const { open, setOpen, disabled: isDisabled } = context;

  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDisabled) {
      setOpen(!open);
    }
  };

  return (
    <button
      type="button"
      className={`${styles.trigger} ${className}`}
      style={style}
      onClick={handleClick}
      aria-expanded={open}
      aria-haspopup="listbox"
      disabled={isDisabled}
    >
      {children}
      <img
        src="/icons/arrow-down.svg"
        alt="categoryicon"
        style={{ width: "12px", height: "12px" }}
      />
    </button>
  );
}

export function SelectValue({ placeholder, className = "", children }) {
  const context = React.useContext(SelectContext);

  if (!context) {
    throw new Error("SelectValue must be used within a Select");
  }

  const { value } = context;

  return (
    <span className={`${styles.value} ${className}`}>
      {children || value|| placeholder }
    </span>
  );
}

// Add SelectSearch component
export function SelectSearch({ placeholder = "Search...", className = "" }) {
  const context = React.useContext(SelectContext);
  if (!context) {
    throw new Error("SelectSearch must be used within a Select");
  }
  const { search, setSearch } = context;
  return (
    <input
      type="text"
      className={styles.searchInput}
      value={search}
      onChange={(e) => setSearch(e.target.value)}
      placeholder={placeholder}
    />
  );
}

export function SelectGroup({ label, className = "", children }) {
  return (
    <div
      className={`${styles.group} ${className}`}
      role="group"
      aria-label={label}
    >
      <div className={styles.groupLabel}>{label}</div>
      {children}
    </div>
  );
}

export function SelectContent({
  className = "",
  children,
  searchField = false,
}) {
  const context = React.useContext(SelectContext);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  if (!context) {
    throw new Error("SelectContent must be used within a Select");
  }

  const {
    open,
    contentRef,
    position,
    zIndex,
    search,
    dropdownDirection,
    dropdownTop,
    disabled: isDisabled,
  } = context;

  if (!open || isDisabled || !mounted) return null;

  // Filter children robustly
  const childrenArray = React.Children.toArray(children);
  let filteredChildren = childrenArray;
  if (search) {
    filteredChildren = childrenArray.filter((child) => {
      if (
        typeof child === "object" &&
        child &&
        child.props &&
        child.props.children
      ) {
        const label = extractTextFromChildren(child.props.children);
        return label.toLowerCase().includes(search.toLowerCase());
      }
      return true;
    });
  }

  const content = (
    <div
      ref={contentRef}
      className={`${styles.content} ${className}`}
      role="listbox"
      style={{
        position: "absolute",
        zIndex: zIndex,
        width: position.width + "px",
        top: dropdownTop + "px",
        left: position.left + "px",
        // padding: "5px",
      }}
    >
      {searchField && <SelectSearch />}

      {filteredChildren && filteredChildren.length > 0 ? (
        filteredChildren
      ) : (
        <div style={{ padding: 8, color: "#888", fontSize: "12px" }}>
          No options found
        </div>
      )}
    </div>
  );

  return createPortal(content, document.body);
}

export function SelectItem({ value, className = "", children, disabled = false }) {
  const context = React.useContext(SelectContext);

  if (!context) {
    throw new Error("SelectItem must be used within a Select");
  }

  const { value: selectedValue, onChange } = context;
  const isSelected = selectedValue === value;

  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return; // 👈 block click if disabled
    onChange(value);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      e.stopPropagation();
      if (disabled) return; // 👈 block keyboard too
      onChange(value);
    }
  };

  return (
    <div
      className={`${styles.item} ${className}`}
      role="option"
      aria-selected={isSelected}
      aria-disabled={disabled}        // 👈 accessibility
      data-selected={isSelected}
      data-disabled={disabled}        // 👈 for CSS styling
      onClick={handleClick}
      tabIndex={disabled ? -1 : 0}   // 👈 remove from tab order if disabled
      onKeyDown={handleKeyDown}
    >
      {children}
    </div>
  );
}